import React, { useState, useEffect } from 'react';
import {
  ReactiveBase,
  SearchBox,
  ReactiveList,
  SelectedFilters,
} from '@appbaseio/reactivesearch';
import EventSearchResult from './EventSearchResult';
import DatePicker from '../../components/DatePicker/DatePicker';
import Moment from 'moment';
import { extendMoment } from 'moment-range';
import { rrulestr } from 'rrule';
import { sendEventSearchToGA4 } from '../../components/GA4';
import Slider from 'react-slick';
import { useMediaQuery } from 'react-responsive';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const moment = extendMoment(Moment);

moment.locale(drupalSettings.path.currentLanguage);

const searchUrl = drupalSettings.base_url + '/node-search/';
let searchIndex;
let primaryColor;

const urlParams = new URLSearchParams(window.location.search);

switch (drupalSettings.active_site) {
  case 'Pyha':
    searchIndex = process.env.PYHA_ES_EVENTS_SEARCH_INDEX_NAME;
    primaryColor = '#003764';
    break;

  case 'Luosto':
    searchIndex = process.env.LUOSTO_ES_EVENTS_SEARCH_INDEX_NAME;
    primaryColor = '#677F17';
    break;

  default:
    searchIndex = process.env.RUKA_ES_EVENTS_SEARCH_INDEX_NAME;
    primaryColor = '#fb0202';
}

searchIndex += `_${drupalSettings.current_language}`;

const EventsSearch = () => {
  const [startDate, setStartDate] = useState(
    urlParams.get('startDate')
      ? moment(urlParams.get('startDate'), 'YYYY-MM-DD')
      : undefined,
  );
  const [endDate, setEndDate] = useState(
    urlParams.get('endDate')
      ? moment(urlParams.get('endDate'), 'YYYY-MM-DD')
      : undefined,
  );

  const [totalResultsCount, setTotalResultsCount] = useState();
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchAction, setSearchAction] = useState();
  const dataLayer = window.dataLayer;

  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 920px)' });

  const sliderSettings = isTabletOrMobile
    ? {
        dots: false,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        className: 'center',
        centerPadding: '10%',
      }
    : {
        dots: false,
        speed: 500,
        slidesToShow: 4,
        slidesToScroll: 1,
      };

  const API_DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
  const UTC_DATE_FORMAT = 'YYYYMMDDTHHmmss[Z]';

  const searchFilterDate = new Date().toISOString().slice(0, -5);
  const customQuery = function () {
    return {
      query: {
        bool: {
          filter: [
            {
              range: {
                end_date: { gte: searchFilterDate },
              },
            },
          ],
        },
      },
    };
  };

  function filterSelectedDates({ start_date, end_date, rrule }) {
    if (!startDate || !endDate) {
      return true;
    }

    if (!start_date || !end_date) {
      return false;
    }

    // rrule needs dates in very specific format.
    const startDateUtc = moment(start_date, API_DATE_FORMAT).format(
      UTC_DATE_FORMAT,
    );
    const endDateUtc = moment(end_date, API_DATE_FORMAT).format(UTC_DATE_FORMAT);

    let ruleString = `DTSTART:${startDateUtc}\n${
      rrule && rrule[0].length > 0 ? rrule : 'FREQ=DAILY'
    };UNTIL=${endDateUtc}`;

    // If frequency is set to daily but weekdays are not set, this setting would
    // give an error. Instead we'll treat it as daily frequency.
    if (
      ruleString.includes('RRULE:FREQ=WEEKLY') &&
      ruleString.includes('BYDAY=;')
    ) {
      ruleString = ruleString
        .replace('RRULE:FREQ=WEEKLY', 'RRULE:FREQ=DAILY')
        .replace('BYDAY=;', '');
    }

    const rule = rrulestr(ruleString);

    const occurrences = rule.between(
      new Date(moment(startDate).startOf('day').utc()),
      new Date(moment(endDate).endOf('day').utc()),
      true,
    );

    return occurrences.length > 0;
  }

  function filterOldDates({ end_date }) {
    if (!end_date) {
      return true;
    }
    const compared_end_date = new Date(end_date);
    const today = new Date();
    return compared_end_date.setHours(0, 0, 0, 0) >= today.setHours(0, 0, 0, 0);
  }

  function filteredResults(unfilteredResults) {
    return unfilteredResults.filter(filterOldDates).filter(filterSelectedDates);
  }

  function transformSearchRequest(request) {
    if (
      typeof request.body !== 'string' ||
      !request.url.includes('/_reactivesearch')
    ) {
      return request;
    }

    const body = JSON.parse(request.body);
    if (!Array.isArray(body.query)) {
      return request;
    }

    const query = body.query.filter(
      ({ id, type }) => id !== 'searchbox' || type !== 'suggestion',
    );

    return query.length === body.query.length
      ? request
      : { ...request, body: JSON.stringify({ ...body, query }) };
  }

  function selectToday() {
    setSearchAction('today');
    setStartDate(moment());
    setEndDate(moment());
  }

  function selectTomorrow() {
    setSearchAction('tomorrow');
    setStartDate(moment().add(1, 'days'));
    setEndDate(moment().add(1, 'days'));
  }

  function selectNextWeek() {
    setSearchAction('tomorrow');
    setStartDate(moment());
    setEndDate(moment().add(7, 'days'));
  }

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      sendEventSearchToGA4(
        dataLayer,
        startDate,
        endDate,
        searchKeyword,
        searchAction,
        totalResultsCount,
      );
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [totalResultsCount, searchKeyword, searchAction]);

  useEffect(() => {
    const url = new URL(window.location);

    if (startDate) {
      url.searchParams.set('startDate', startDate.format('YYYY-MM-DD'));
    } else {
      url.searchParams.delete('startDate');
    }

    if (endDate) {
      url.searchParams.set('endDate', endDate.format('YYYY-MM-DD'));
    } else {
      url.searchParams.delete('endDate');
    }

    window.history.pushState({}, '', url);
  }, [startDate, endDate]);

  /**
   * Get all the different tags available, and sort events under them.
   */
  function getEventsByTag(events) {
    if (!events || events.length === 0) {
      return {};
    }

    const groupedByTag = events.reduce((acc, item) => {
      const itemTags =
        item.tag && item.tag.length > 0 ? item.tag : [Drupal.t('Other')];

      itemTags.forEach((tag) => {
        if (!acc[tag]) {
          acc[tag] = [];
        }
        acc[tag].push(item);
      });

      return acc;
    }, {});

    // Don't show the sustainability tag category.
    if (drupalSettings.sustainability_tag) {
      delete groupedByTag[drupalSettings.sustainability_tag];
    }

    return groupedByTag;
  }

  function sortEventsByWeight(a, b) {
    const tagA = drupalSettings.event_tags_by_weight.find(
      (tag) => tag.title === a[0],
    );
    const tagB = drupalSettings.event_tags_by_weight.find(
      (tag) => tag.title === b[0],
    );

    const aWeight = tagA?.weight ?? 99;
    const bWeight = tagB?.weight ?? 99;

    return aWeight - bWeight;
  }

  return (
    <ReactiveBase
      app={searchIndex}
      url={searchUrl}
      enableAppbase={true}
      headers={{
        token: `${process.env.RUKA_ES_AUTH_TOKEN}`,
      }}
      reactivesearchAPIConfig={{
        recordAnalytics: false,
        emptyQuery: false,
      }}
      transformRequest={transformSearchRequest}
      theme={{
        typography: {
          fontFamily: 'helvetica neue,arial,sans-serif',
        },
        colors: {
          textColor: '#223',
          primaryColor,
          borderColor: '#dcdcda',
        },
      }}
    >
      <div className="eventsearch__form">
        <div className="eventsearch__form__filter-wrapper">
          <div>
            <label className="eventsearch__form__label">
              {Drupal.t('Dates')}
            </label>
            <div className="eventsearch__form__date-wrapper">
              <DatePicker
                startDate={startDate}
                endDate={endDate}
                startDateId={'ruka_start_date'}
                endDateId={'ruka_end_date'}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                allowOneSingleDaySelect={true}
                useMoment={true}
              />
            </div>
          </div>

          <div className="eventsearch__form__date-button-wrapper">
            <button
              onClick={() => selectToday()}
              className="eventsearch__form__select-single-date"
            >
              {Drupal.t('Today')}
            </button>
            <button
              onClick={() => selectTomorrow()}
              className="eventsearch__form__select-single-date"
            >
              {Drupal.t('Tomorrow')}
            </button>
            <button
              onClick={() => selectNextWeek()}
              className="eventsearch__form__select-single-date"
            >
              {Drupal.t('Next 7 days')}
            </button>
          </div>

          <div className="eventsearch__form--filters">
            <SearchBox
              componentId="searchbox"
              dataField={[
                {
                  field: 'title',
                  weight: 2,
                },
                {
                  field: 'title.autosuggest',
                  weight: 1,
                },
              ]}
              placeholder={Drupal.t('Search...')}
              filterLabel={Drupal.t('Keyword')}
              className="eventsearch__form__searchbox"
              autosuggest={false}
              onValueChange={(value) => {
                setSearchKeyword(() => value);
              }}
            />
          </div>
        </div>
      </div>
      <ReactiveList
        componentId="results"
        dataField="start_date"
        defaultQuery={customQuery}
        compoundClause={true}
        showResultStats={false}
        size={999}
        scrollOnChange={false}
        loader=" "
        sortBy="asc"
        react={{
          and: ['searchbox'],
        }}
        renderNoResults={() => ''}
        render={({ data }) => {
          const filteredData = filteredResults(data);
          return (
            <>
              {setTotalResultsCount(filteredData.length)}
              <SelectedFilters
                clearAllLabel={Drupal.t('Clear all filters')}
                className="eventsearch__results__filters"
                innerClass={{
                  button: 'eventsearch__results__filters__item',
                }}
              />
              <p className="eventsearch__results__count">
                {totalResultsCount}{' '}
                {totalResultsCount == 1
                  ? Drupal.t('event')
                  : Drupal.t('events')}
              </p>
              <div className="eventsearch__results-wrapper">
                {filteredData &&
                  filteredData.length > 0 &&
                  Object.entries(getEventsByTag(filteredData))
                    .sort(sortEventsByWeight)
                    .map(([tag, events], i) => {
                      return (
                        <div key={i} className="eventsearch-results-by-tag">
                          <h3 className="eventsearch-results-by-tag__event-title">
                            {tag}
                          </h3>
                          <div className="eventsearch-results-by-tag-event-list">
                            <Slider
                              {...sliderSettings}
                              infinite={
                                isTabletOrMobile
                                  ? events && events.length > 1
                                  : events && events.length > 4
                              }
                              centerMode={
                                isTabletOrMobile && events && events.length > 1
                              }
                            >
                              {events.map((event) => {
                                // There should always be an image, but
                                // adding checks just in case.
                                let image1 = null;
                                let image2 = null;
                                if (
                                  event.hero_image_1 &&
                                  event.hero_image_1[0]
                                ) {
                                  image1 = event.hero_image_1[0];
                                }
                                if (
                                  event.hero_image_2 &&
                                  event.hero_image_2[0]
                                ) {
                                  image2 = event.hero_image_2[0];
                                } else {
                                  image2 = image1;
                                }

                                return (
                                  <EventSearchResult
                                    key={event.nid}
                                    title={event.title}
                                    description={
                                      event.field_description &&
                                      event.field_description[0]
                                        ? event.field_description[0]
                                        : null
                                    }
                                    url={event.url}
                                    imageUrl={event.image_url}
                                    images={[image1, image2]}
                                    startDate={event.start_date}
                                    endDate={event.end_date}
                                    category={event.category}
                                    tags={event.tag}
                                    price={event.field_price}
                                  />
                                );
                              })}
                            </Slider>
                          </div>
                        </div>
                      );
                    })}
              </div>
            </>
          );
        }}
      />
    </ReactiveBase>
  );
};

export default EventsSearch;
